import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallow } from 'zustand/shallow';

import {
  Close,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  VFlex,
  VIconButton,
  VLoadingSpinner,
  VStack,
  blobToBase64,
  cn,
  useFlag,
  useWatch,
} from '@/package';
import {
  type __CES2026__CameraNormalizedConfig,
  useFrameImageViewer,
} from '@/provider';
import { useImageCacheManager } from '@/provider/frameImageViewer/hook';
import type { FrameData, Task } from '@/type';

import {
  bufferToBitmap,
  drawImageOnCanvasFromBitmap,
  drawImageOnCanvasFromBuffer,
} from '../util';

type CamPopupProps = {
  cameraCalibrationConfigMap: {
    [
      imageFileName: __CES2026__CameraNormalizedConfig['name']
    ]: __CES2026__CameraNormalizedConfig;
  };
  frameSeq: FrameData['frameSeq'];
  imagePathList: FrameData['imagePath'];
  labelingBoxList: FrameData['workData'];
  selectedImageIndex: number;
  selectedLabelingBoxIdList: FrameData['workData'][0]['id'][];
  selectedTask: Task;
  onSelectBox?(id: FrameData['workData'][0]['id']): void;
};

const MAX_VISIBLE_THUMBNAILS = 5.5;

export const CamPopup = ({
  cameraCalibrationConfigMap,
  frameSeq,
  imagePathList,
  labelingBoxList,
  selectedImageIndex,
  selectedLabelingBoxIdList,
  selectedTask,
  onSelectBox,
}: CamPopupProps) => {
  const { t } = useTranslation();
  const { __CES2026__generateLabelingBoxListRenderer } = useFrameImageViewer();
  const cacheManager = useImageCacheManager();
  const [innerSelectedImageIndex, setInnerSelectedImageIndex] =
    useState(selectedImageIndex);
  const [fetchedImageMap, setFetchedImageMap] = useState(
    {} as Dictionary<{
      isLoading: boolean;
      url: string;
    }>,
  );
  const cameraCalibrationConfig = useMemo(() => {
    if (!imagePathList?.length) return;

    const imagePath = imagePathList[innerSelectedImageIndex];
    const imageFileName = imagePath.split('/').pop() ?? '';

    const cameraCalibrationConfig =
      cameraCalibrationConfigMap[imageFileName.replace(/(\.)([a-z]+)$/i, '')];

    return cameraCalibrationConfig;
  }, [cameraCalibrationConfigMap, imagePathList, innerSelectedImageIndex]);
  const selectedImagePath = useMemo(
    () => imagePathList?.[innerSelectedImageIndex],
    [innerSelectedImageIndex, imagePathList],
  );
  const {
    value: isSelectedImageLoading,
    setFalse: loadedSelectedImage,
    setTrue: loadingSelectedImage,
  } = useFlag(true);
  const frameSeqRef = useRef(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const mainCanvasRef = useRef<HTMLCanvasElement>(null);
  const imageListRef = useRef<HTMLDivElement>(null);

  const downloadImage = (imagePath: string) => {
    return cacheManager
      .getImage(imagePath)
      .then(({ buffer, height, width }) =>
        bufferToBitmap(buffer, { height, width }),
      )
      .then(async bitmap => {
        const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);

        drawImageOnCanvasFromBitmap(canvas, bitmap);

        bitmap.close();

        return canvas.convertToBlob({ type: 'image/png' });
      })
      .then(blobToBase64);
  };

  useWatch(
    imagePathList,
    () => {
      if (!imagePathList) return;

      const fetchedImage = imagePathList.reduce(
        (acc, cur) => {
          return {
            ...acc,
            [cur]: { isLoading: true, url: '' },
          };
        },
        {} as Dictionary<{
          isLoading: boolean;
          url: string;
        }>,
      );

      setFetchedImageMap(fetchedImage);

      imagePathList.forEach(imagePath => {
        downloadImage(imagePath).then(base64 => {
          // image fetching 중 frameSeq 가 변경 됐으면 아무런 동작하지 않아야 함
          if (frameSeqRef.current !== frameSeq) return;

          fetchedImage[imagePath].isLoading = false;
          fetchedImage[imagePath].url = base64;

          setFetchedImageMap({ ...fetchedImage });
        });
      });
    },
    {
      immediate: true,
      equalityFn: shallow,
    },
  );

  useEffect(() => {
    frameSeqRef.current = frameSeq;
  }, [frameSeq]);

  useEffect(() => {
    const loadImage = async () => {
      if (!mainCanvasRef.current || !selectedImagePath) return;

      loadingSelectedImage();

      try {
        mainCanvasRef.current.width = mainCanvasRef.current.offsetWidth;
        mainCanvasRef.current.height = mainCanvasRef.current.offsetHeight;

        const { buffer, height, width } =
          await cacheManager.getImage(selectedImagePath);

        drawImageOnCanvasFromBuffer(mainCanvasRef.current, {
          buffer,
          height,
          width,
        });
      } catch (err) {
        console.group('[CamPopup] loadImage error');
        console.warn(err);
        console.groupEnd();
      }

      setTimeout(loadedSelectedImage, 10);
    };

    loadImage();
  }, [selectedImagePath]);

  useEffect(() => {
    if (
      !imageListRef.current ||
      imageListRef.current.offsetWidth > imageListRef.current.scrollWidth
    )
      return;

    const $selectedImage = imageListRef.current.querySelector<HTMLDivElement>(
      '[aria-selected="true"]',
    );

    if (!$selectedImage) return;

    imageListRef.current.scroll({
      left:
        $selectedImage.offsetLeft -
        imageListRef.current.offsetLeft -
        (imageListRef.current.offsetWidth - $selectedImage.offsetWidth) / 2,
      top: 0,
      behavior: 'smooth',
    });
  }, [innerSelectedImageIndex]);

  useEffect(() => {
    if (
      !cameraCalibrationConfig ||
      !selectedTask ||
      !mainCanvasRef.current ||
      isSelectedImageLoading
    )
      return;

    const { cleanup } = __CES2026__generateLabelingBoxListRenderer({
      cameraCalibrationConfig,
      labelingBoxList,
      selectedLabelingBoxIdList,
      selectedTask,
    }).render(mainCanvasRef.current, {
      boxClick(boxID) {
        onSelectBox?.(boxID);
      },
    });

    return () => cleanup();
  }, [
    cameraCalibrationConfig,
    labelingBoxList,
    isSelectedImageLoading,
    selectedLabelingBoxIdList,
    selectedTask,
  ]);

  return (
    <VStack ref={rootRef} className="max-w-full bg-white p-1" gap={0.25}>
      <VFlex items="center">
        <div className="flex-1 text-body-lg font-semibold text-gray-500">
          {selectedImagePath?.split('/').pop() ??
            t('labeling.imagePathIsNotFound.thirster')}
        </div>
        <VIconButton
          Icon={Close}
          aria-label="close-cam-image-popup-button"
          className="text-gray-400"
          size="sm"
          variant="roundSecondary"
          onClick={() => window.close()}
        />
      </VFlex>

      <div aria-label="cam-image-wrapper">
        <VFlex
          aria-label="selected-cam-image-canvas"
          className={cn('h-auto w-full', { relative: !selectedImagePath })}
          justify="center"
          items="center"
        >
          {!selectedImagePath && (
            <div className="absolute flex h-full w-full items-center justify-center bg-black/65 text-white">
              {t('labeling.imagePathIsNotFound.thirster')}
            </div>
          )}
          <canvas
            className="aspect-[16/9] h-auto w-full rounded-sm bg-black"
            ref={mainCanvasRef}
            width={640}
            height={(640 * 9) / 16}
          />
        </VFlex>
      </div>

      <VFlex aria-label="cam-image-gallery" justify="between" gap={0.5}>
        <VFlex aria-label="prev-image-button" items="center">
          <VIconButton
            Icon={KeyboardArrowLeft}
            className="text-primary-500"
            disabled={innerSelectedImageIndex === 0}
            size="lg"
            variant="roundSecondary"
            onClick={() => setInnerSelectedImageIndex(v => v - 1)}
          />
        </VFlex>

        <VFlex
          className="w-fit overflow-hidden"
          ref={imageListRef}
          style={{
            maxWidth: `${MAX_VISIBLE_THUMBNAILS * 100 + (MAX_VISIBLE_THUMBNAILS - 1) * 4}px`,
          }}
          gap={0.25}
        >
          {Object.entries(fetchedImageMap)?.map(
            ([imagePath, { url, isLoading }], i) => {
              return (
                <VFlex
                  key={imagePath}
                  aria-selected={i === innerSelectedImageIndex}
                  className={cn(
                    'relative box-border h-[69px] w-[100px] min-w-[100px] overflow-hidden rounded-md bg-black',
                    i === innerSelectedImageIndex
                      ? ['border-4 border-blue-300']
                      : ['cursor-pointer'],
                  )}
                  items="center"
                  justify="center"
                  onClick={() => {
                    setInnerSelectedImageIndex(i);
                  }}
                >
                  <img
                    className={cn(
                      'h-auto max-h-full w-auto max-w-full transition-transform hover:scale-110',
                      {
                        'hidden opacity-0': isLoading,
                      },
                    )}
                    alt={imagePath}
                    src={url}
                  />
                  {isLoading && (
                    <VFlex
                      className="absolute top-0 left-0 z-10 size-full"
                      justify="center"
                      items="center"
                    >
                      <VLoadingSpinner className="size-4 text-status-inProgress" />
                    </VFlex>
                  )}
                </VFlex>
              );
            },
          )}
        </VFlex>

        <VFlex aria-label="next-image-button" items="center">
          <VIconButton
            Icon={KeyboardArrowRight}
            className="text-primary-500"
            disabled={
              imagePathList?.length
                ? innerSelectedImageIndex === imagePathList.length - 1
                : true
            }
            size="lg"
            variant="roundSecondary"
            onClick={() => setInnerSelectedImageIndex(v => v + 1)}
          />
        </VFlex>
      </VFlex>
    </VStack>
  );
};

CamPopup.displayName = 'CamPopup';
